import { useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { homeFor } from '@/lib/home-route';
import { serverCode, serverMessage } from '@/lib/serverMessage';
import { api } from '@/services/api';
import { AuthShell } from '@/components/auth/AuthShell';
import {
  AuthHeading,
  EditorialField,
  MonoLabel,
  PillButton,
  PillLink,
} from '@/components/editorial';

// Built per render locale (#2109 L6): the validation messages are copy.
const makeResetPasswordSchema = (t: TFunction<'auth'>) =>
  z
    .object({
      password: z
        .string()
        .min(8, t('reset.passwordMin'))
        .regex(/[A-Z]/, t('reset.passwordUppercase'))
        .regex(/[a-z]/, t('reset.passwordLowercase'))
        .regex(/[0-9]/, t('reset.passwordNumber')),
      confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('reset.passwordsMismatch'),
      path: ['confirmPassword'],
    });

type ResetPasswordFormData = z.infer<ReturnType<typeof makeResetPasswordSchema>>;

export function ResetPasswordPage() {
  // B-169: like /login (B-168) and /apply (GH-271.1), this page only READS
  // auth state — no token paths, no AuthContext changes.
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [resetSuccess, setResetSuccess] = useState(false);
  // The server said this one-time link was already spent (#2729).
  const [linkUsed, setLinkUsed] = useState(false);
  // A signed-in user who clicked the emailed link on purpose can opt past
  // the interstitial and complete the reset.
  const [proceedAnyway, setProceedAnyway] = useState(false);
  // One-way latch: once the form (or the token/success screens) has rendered
  // on this mount, it stays. Load-bearing here: POST /auth/reset-password
  // revokes ALL refresh tokens on success, so auth state can flip mid-mount
  // right after a reset — that flip must not yank the success screen (or an
  // in-progress form) away.
  const formShownRef = useRef(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const resetPasswordSchema = useMemo(() => makeResetPasswordSchema(t), [t]);

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      toast({
        variant: 'destructive',
        title: t('reset.invalidLinkTitle'),
        description: t('reset.invalidLinkDescription'),
      });
      return;
    }

    try {
      await api.auth.resetPassword(token, data.password);
      setResetSuccess(true);
      toast({
        title: t('reset.successTitle'),
        description: t('reset.successDescription'),
      });
    } catch (error: unknown) {
      // A spent or expired link gets its own screen with a way forward,
      // in the reader's language, instead of a toast (#2729).
      if (serverCode(error) === 'RESET_LINK_USED_OR_EXPIRED') {
        setLinkUsed(true);
        return;
      }
      toast({
        variant: 'destructive',
        title: t('reset.failedTitle'),
        description: serverMessage(error, t('reset.failedDescription')),
      });
    }
  };

  // B-169: a signed-in user landing here (usually from the emailed link) sees
  // their session acknowledged before any form. Ordered ahead of the !token
  // screen: for a signed-in user, "go to my account" beats "link expired".
  if (!formShownRef.current) {
    if (loading) {
      return (
        <div
          className="flex min-h-screen items-center justify-center"
          role="status"
          aria-label={t('shared.checkingSession')}
        >
          <Loader2 className="h-6 w-6 animate-spin text-stevens-indigo" />
        </div>
      );
    }

    if (user && !proceedAnyway) {
      return (
        <AuthShell
          documentLabel={t('reset.documentLabel')}
          meta={[{ k: t('shared.signedInAs'), v: user.email ?? t('shared.yourAccount') }]}
        >
          <header className="flex flex-col gap-4 mb-10">
            <MonoLabel>{t('shared.alreadySignedIn')}</MonoLabel>
            <AuthHeading>
              {t('shared.alreadySignedInTitle')}
            </AuthHeading>
            <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
              <Trans
                t={t}
                i18nKey={token ? 'reset.interstitialLedeWithToken' : 'reset.interstitialLedeNoToken'}
                values={{ email: user.email ?? t('shared.yourAccount') }}
                components={{ strong: <strong className="font-medium text-stevens-ink" /> }}
              />
            </p>
          </header>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <PillButton type="button" onClick={() => navigate(homeFor(user.role), { replace: true })}>
              {t('shared.goToMyAccount')} {"→"}
            </PillButton>
            {token ? (
              <PillButton type="button" variant="ghost" onClick={() => setProceedAnyway(true)}>
                {t('reset.continueWithReset')}
              </PillButton>
            ) : (
              <Link
                to="/forgot-password"
                className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors"
              >
                {t('reset.requestNewResetLink')}
              </Link>
            )}
          </div>
        </AuthShell>
      );
    }
  }
  formShownRef.current = true;

  // A spent link (#2729) reads the same as a missing or expired one: the
  // link is no longer valid, with sign-in and a new link both on the page.
  if ((!token || linkUsed) && !resetSuccess) {
    return (
      <AuthShell documentLabel={t('reset.invalidDocumentLabel')}>
        <header className="flex flex-col gap-4 mb-8">
          <MonoLabel>{t('reset.invalidEyebrow')}</MonoLabel>
          <AuthHeading>
            {t('reset.invalidTitle')}
          </AuthHeading>
          <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
            {t('reset.invalidLede')}
          </p>
        </header>

        <div className="flex items-center gap-3">
          <PillLink href="/forgot-password" className="no-underline">
            {t('reset.requestNewLink')} {"→"}
          </PillLink>
          <Link
            to="/login"
            className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors"
          >
            {t('reset.backToSignIn')}
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (resetSuccess) {
    return (
      <AuthShell documentLabel={t('reset.successDocumentLabel')}>
        <header className="flex flex-col gap-4 mb-8">
          <MonoLabel>{t('reset.successEyebrow')}</MonoLabel>
          <AuthHeading>
            {t('reset.successHeading')}
          </AuthHeading>
          <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
            {t('reset.successLede')}
          </p>
        </header>

        <PillButton onClick={() => navigate('/login')}>{t('reset.continueToSignIn')} {"→"}</PillButton>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      documentLabel={t('reset.documentLabel')}
      meta={[{ k: t('reset.stepLabel'), v: t('reset.stepValue') }]}
    >
      <header className="flex flex-col gap-4 mb-10">
        <MonoLabel>{t('reset.eyebrow')}</MonoLabel>
        <AuthHeading>
          <Trans t={t} i18nKey="reset.title" components={{ em: <em className="not-italic text-stevens-indigo" /> }} />
        </AuthHeading>
        <p className="text-card-title leading-[1.6] text-stevens-ink-2 max-w-[52ch] m-0">
          {t('reset.lede')}
        </p>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6" noValidate>
        <EditorialField
          label={t('reset.newPasswordLabel')}
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.password?.message}
          helper={t('reset.newPasswordHelper')}
          {...register('password')}
        />
        <EditorialField
          label={t('reset.confirmPasswordLabel')}
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />

        <div className="flex items-center gap-3 mt-2">
          <PillButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('reset.resetting')}
              </>
            ) : (
              <>{t('reset.submit')} {"→"}</>
            )}
          </PillButton>
          <Link
            to="/login"
            className="font-sans text-body text-stevens-ink-2 hover:text-stevens-ink transition-colors"
          >
            {t('reset.backToSignIn')}
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
